import { canDismissCursorTerminalStatus } from "./agent-cursor-terminal-status";
import { resolveCursorConversationId } from "@/desktop/cursor-conversation-id";
import {
  runSurface,
  useRunSurfaceContext,
} from "@/desktop/run-surface-context";
import { isActiveGuideCursor } from "@/lib/presentation-guide-cursor-lifetime";
import { cursorRunIdentity } from "@/lib/run-surface-owner";
import { useEffect, useRef, useMemo, useState } from "react";
import { useCanvasStore } from "@/stores/canvas-store";
import { getCurrentDenkerWs } from "@/api/canvas-ws-ref";
import { AnimatedCursor } from "@/components/cursors/agent-cursor-layer-animated-cursor";
import {
  canvasCursorVisibility,
  isActiveCursorExecution,
} from "@/components/cursors/agent-cursor-layer-visibility";
import type { AgentCursor as AgentCursorType } from "@/stores/canvas-store";
import {
  ensureUserPointerTracker,
  getLatestUserPointer,
} from "@/components/cursors/user-pointer-tracker";
import { shouldSuppressFrameOwnedCursorStatus } from "@/components/cursors/agent-cursor-intent";

/** Offset applied below+right of the user pointer at spawn so the agent
 *  cursor lands "near" the pointer without occluding it. */
const SPAWN_USER_POINTER_OFFSET_X = 24;
const SPAWN_USER_POINTER_OFFSET_Y = 24;

/** Safety-net: inactive cursors not updated within this window are removed.
 *  Primary removal is the owner connection's timer on execution.completed. */
const STALE_TIMEOUT_MS = 10_000;
/** How often to check for stale cursors. */
const CLEANUP_INTERVAL_MS = 5_000;
/** Soft client-side stall threshold — when a cursor's `lastSeen` hasn't
 *  refreshed in this long AND the underlying execution is still running,
 *  surface a "no reply yet" warning chip with a Cancel affordance. Picks
 *  up CLI hangs (model rejection, OpenAI silent stalls) that the
 *  companion's own 5-minute timeout is too slow to expose. */
const STALL_WARNING_MS = 75_000;
/** Cadence for re-evaluating stall — cheap; re-renders only this layer. */
const STALL_TICK_MS = 5_000;
/**
 * Agent cursor layer — reads agent cursors from the canvas store and
 * drives position via rAF animation (idle drift, bezier travel, staged working sequence, resting).
 * Cursor removal is driven by the owner connection (10s timer on
 * execution.completed, `desktop/use-owner-connection.ts`);
 * a {@link STALE_TIMEOUT_MS} safety-net catches missed events.
 *
 * Cursor keys are runtime_session_id. One runtime segment = one cursor.
 */
export function AgentCursorLayer({
  onSwitchAgentModel,
}: {
  /** Open the agent's settings panel — used by the cursor stall chip's
   *  "Switch model" button. Receives the cursor's display name; the
   *  caller resolves it to the project agent and opens the right panel. */
  onSwitchAgentModel?: (agentName: string) => void;
} = {}) {
  // One read of the run surfaces for every cursor. Its agent sessions map
  // a stalled cursor to the execution its Cancel button stops.
  const surfaceContext = useRunSurfaceContext();
  const { agentCursors, activeGuideId, agentSessions, executions } =
    surfaceContext;
  const removeAgentCursor = useCanvasStore((s) => s.removeAgentCursor);

  // Tick state — re-renders this layer every STALL_TICK_MS so the stall
  // chip's elapsed time stays current without forcing other components
  // to re-render. Reset whenever a cursor moves (lastSeen changes).
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), STALL_TICK_MS);
    return () => clearInterval(interval);
  }, []);
  // Track which cursors are "new" (just appeared) for fade-in.
  // Entries are removed on idle cleanup so reconnections get a fresh fade-in.
  const knownCursorIds = useRef<Set<string>>(new Set());

  // Spawn anchor captured from the user pointer at the moment each
  // cursor first appears. Frozen for the lifetime of that cursor — the
  // agent cursor never follows the user pointer afterwards.
  const spawnAnchors = useRef<Map<string, { x: number; y: number }>>(new Map());

  // Mount the global pointer tracker once.
  useEffect(() => {
    ensureUserPointerTracker();
  }, []);

  // Refs for cleanup interval (avoids re-creating interval on every render)
  const agentCursorsRef = useRef(agentCursors);
  agentCursorsRef.current = agentCursors;
  const removeRef = useRef(removeAgentCursor);
  removeRef.current = removeAgentCursor;

  // Safety-net cleanup for cursors that were never explicitly removed
  // (e.g. execution.completed was missed due to a WebSocket disconnect).
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      for (const [agentId, cursor] of agentCursorsRef.current) {
        // Cursors with a pending cursor-anchored question must stay
        // visible until the user answers — skip the staleness sweep.
        if (cursor.question || isActiveGuideCursor(cursor)) continue;
        const age = now - new Date(cursor.lastSeen).getTime();
        if (age > STALE_TIMEOUT_MS) {
          if (isActiveCursorExecution(cursor)) continue;
          removeRef.current(agentId);
          knownCursorIds.current.delete(agentId);
          spawnAnchors.current.delete(agentId);
        }
      }
    }, CLEANUP_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const cursorEntries = useMemo(() => {
    const result: Array<{
      cursor: AgentCursorType;
      bandIndex: number;
      isNew: boolean;
      spawnAnchor: { x: number; y: number } | null;
      resolvedColor: string;
      stalledSinceMs?: number;
      suppressStatus?: boolean;
      owningConversationFrameId?: string | null;
      ownsQuestion: boolean;
      executionId?: string | null;
    }> = [];

    const frameCounts = new Map<string, number>();

    for (const cursor of agentCursors.values()) {
      const { visible, owningConversationFrameId } = canvasCursorVisibility(
        cursor,
        surfaceContext,
      );
      if (!visible) continue;

      const bareName = (cursor.name ?? "").split(" · ")[0]?.toLowerCase();
      const sessionKey = `${bareName ?? ""}:${cursor.spaceId}`;
      const linkedSession = agentSessions[sessionKey] ?? null;

      const key = cursor.targetFrameId ?? "__idle__";
      const index = frameCounts.get(key) ?? 0;
      frameCounts.set(key, index + 1);

      const isNewCursor = !knownCursorIds.current.has(cursor.agentId);
      if (isNewCursor) {
        knownCursorIds.current.add(cursor.agentId);
        // Capture the initial user pointer so the cursor has a sensible
        // spawn point before the live-pointer tracker starts feeding frames.
        const pointer = getLatestUserPointer();
        if (pointer) {
          spawnAnchors.current.set(cursor.agentId, {
            x: pointer.x + SPAWN_USER_POINTER_OFFSET_X,
            y: pointer.y + SPAWN_USER_POINTER_OFFSET_Y,
          });
        }
      }

      // Use the backend-provided template color directly. Agent colors are
      // persisted user/project identity, so cursors must not derive a fresh
      // display color from generated avatar SVGs on each load.
      const resolvedColor = cursor.color;

      const lastSeenMs = new Date(cursor.lastSeen).getTime();
      const idleSince = now - lastSeenMs;
      const cursorIsIdle =
        !cursor.toolCall &&
        !cursor.content &&
        !cursor.error &&
        !cursor.question;
      // Cursor key is runtime_session_id; look up the matching
      // active execution by agent name + space so the Cancel button
      // can target the right exec_id. Reuses `bareName` derived above
      // for the avatar-color lookup.
      const stalledSinceMs =
        cursorIsIdle && idleSince > STALL_WARNING_MS && linkedSession
          ? idleSince
          : undefined;

      result.push({
        cursor,
        bandIndex: index,
        isNew: isNewCursor,
        spawnAnchor: spawnAnchors.current.get(cursor.agentId) ?? null,
        resolvedColor,
        stalledSinceMs,
        owningConversationFrameId,
        suppressStatus: shouldSuppressFrameOwnedCursorStatus(
          cursor,
          owningConversationFrameId !== null || surfaceContext.dockEnabled,
        ),
        ownsQuestion:
          !!cursor.question &&
          runSurface(
            cursorRunIdentity(
              cursor,
              resolveCursorConversationId(cursor, executions, agentSessions),
            ),
            surfaceContext,
            cursor.question.awaitingInputId,
          ) === "cursor",
        executionId: linkedSession?.executionId ?? null,
      });
    }

    return result;
  }, [agentCursors, agentSessions, executions, surfaceContext, now]);

  if (cursorEntries.length === 0) return null;

  return (
    <div
      className="pointer-events-none absolute inset-0 z-50 overflow-hidden"
      data-testid="agent-cursor-overlay"
    >
      {cursorEntries.map((entry) => (
        <AnimatedCursor
          key={entry.cursor.agentId}
          cursor={entry.cursor}
          bandIndex={entry.bandIndex}
          isNew={entry.isNew}
          spawnAnchor={entry.spawnAnchor}
          resolvedColor={entry.resolvedColor}
          stalledSinceMs={entry.stalledSinceMs}
          suppressStatus={entry.suppressStatus}
          owningConversationFrameId={entry.owningConversationFrameId}
          ownsQuestion={entry.ownsQuestion}
          onCancel={
            entry.stalledSinceMs !== undefined && entry.executionId
              ? () => getCurrentDenkerWs()?.stopExecution(entry.executionId!)
              : undefined
          }
          onSwitchModel={
            entry.stalledSinceMs !== undefined && onSwitchAgentModel
              ? () => onSwitchAgentModel(entry.cursor.name)
              : undefined
          }
          onDismiss={
            entry.cursor.presentation === "point" ||
            canDismissCursorTerminalStatus(entry.cursor, activeGuideId)
              ? () => removeAgentCursor(entry.cursor.agentId)
              : undefined
          }
        />
      ))}
    </div>
  );
}
